import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQueryClient } from "react-query"
import ReversePaymentConfirmationModal from "src/main/Billing/Payments/ReversePaymentConfirmationModal"

import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import Button from "src/components/Button"
import SideSheet from "src/components/SideSheet"
import Tooltip from "src/components/Tooltip"

import { PAYMENT_ICON, formatDay } from "./helpers"

const PaymentHeader = ({ payment }) => {
  const queryClient = useQueryClient()
  const inSheet = SideSheet.useInSideSheet()
  const [isConfirmingReversal, setIsConfirmingReversal] = useState(false)

  const title = payment.displaySequentialId || "Payment"
  const details = [formatDay(payment.paidOn), payment.contactName]

  const reloadPayment = () => queryClient.invalidateQueries(["paymentTxn"])

  // A reversal reopens invoices, so every list or sheet that shows one reloads.
  const reloadAfterReversal = () =>
    [
      ["paymentTxn"],
      ["invoiceDetail"],
      ["invoices"],
      ["invoice_txns"],
      ["posPayments"],
      ["workOrderInvoices"],
      ["workOrder"],
    ].forEach((key) => queryClient.invalidateQueries(key))

  const reverseButton = (
    <Button
      variant="quarternary"
      icon="fa-arrow-u-turn-up-left"
      iconStyle="solid"
      disabled={Boolean(payment.reversePaymentDisabledMessage)}
      onClick={() => setIsConfirmingReversal(true)}
    >
      Reverse payment
    </Button>
  )

  const reverseControl = payment.reversePaymentDisabledMessage ? (
    <Tooltip
      text={payment.reversePaymentDisabledMessage}
      placement="top"
      variant="dark"
      maxWidth="300px"
    >
      {reverseButton}
    </Tooltip>
  ) : (
    reverseButton
  )

  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-300">
          <i
            className={`${PAYMENT_ICON} text-[32px] text-supplementary-green-600`}
            aria-hidden="true"
          />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 text-2xl font-bold text-gray-900">{title}</h1>
            <Badge
              text={payment.statusLabel}
              color={statusToken(payment.status)}
            />
          </div>
          <p className="m-0 mt-1 text-sm text-gray-700">
            {details.filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
      {payment.canReverse &&
        (inSheet ? (
          <SideSheet.HeaderActions>{reverseControl}</SideSheet.HeaderActions>
        ) : (
          reverseControl
        ))}
      <ReversePaymentConfirmationModal
        isOpen={isConfirmingReversal}
        onClose={() => setIsConfirmingReversal(false)}
        onReverseSuccess={() => {
          setIsConfirmingReversal(false)
          reloadAfterReversal()
        }}
        onReverseError={reloadPayment}
        paymentTxnId={payment.id}
        invoices={payment.invoices}
      />
    </header>
  )
}

PaymentHeader.propTypes = {
  payment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displaySequentialId: PropTypes.string,
    status: PropTypes.string.isRequired,
    statusLabel: PropTypes.string.isRequired,
    paidOn: PropTypes.string.isRequired,
    contactName: PropTypes.string,
    reversePaymentDisabledMessage: PropTypes.string,
    canReverse: PropTypes.bool.isRequired,
    invoices: PropTypes.array.isRequired,
  }).isRequired,
}

export default PaymentHeader
